import { ITile, IPosition } from '../types/types';
import {
  GenerationDebug,
  IRoom,
  TileType,
  TileDelta,
} from '../types/map.types';
import { dungeonService } from './DungeonService';
import { isTileBlocking } from '../utils/map.utils';

class MapService {
  private static instance: MapService;
  private mapTiles: ITile[] = [];
  private tileMap = new Map<string, ITile>();
  private sealedOriginals = new Map<string, TileType>();

  /** Set by Dungeon.tsx — bridges a runtime tile mutation back to the React
   * render layer (new `floorTiles` ref → autotile recompute). */
  public onTilesChanged: () => void = () => {};

  /** Set by Dungeon.tsx (host) — replicates runtime tile changes (challenge-room
   * seal/restore) to clients, which apply them via `applyTileDeltas`. Host-guarded
   * inside the callback; a no-op off host. */
  public onTilesMutated: (deltas: TileDelta[]) => void = () => {};

  private rooms: IRoom[] = [];
  private roomIndexMap = new Map<string, number>();
  private _generationDebug: GenerationDebug | null = null;
  public dungeonService!: typeof dungeonService;

  private _discoveredTiles = new Set<string>();

  get discoveredTiles(): ReadonlySet<string> {
    return this._discoveredTiles;
  }

  revealAround(cx: number, cy: number, radius: number): boolean {
    let changed = false;
    const r2 = radius * radius;
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        if (dx * dx + dy * dy <= r2) {
          const key = `${cx + dx},${cy + dy}`;
          if (!this._discoveredTiles.has(key)) {
            this._discoveredTiles.add(key);
            changed = true;
          }
        }
      }
    }
    return changed;
  }

  isTileDiscovered(x: number, y: number): boolean {
    return this._discoveredTiles.has(`${x},${y}`);
  }

  revealAll(): void {
    const tiles = this.getMapTiles();
    for (const tile of tiles) {
      if (tile.type === 'ground') {
        this._discoveredTiles.add(`${tile.x},${tile.y}`);
      }
    }
  }

  clearDiscoveredTiles(): void {
    this._discoveredTiles.clear();
  }

  setDiscoveredTiles(tiles: string[]): void {
    this._discoveredTiles = new Set(tiles);
  }

  getDiscoveredTilesArray(): string[] {
    return Array.from(this._discoveredTiles);
  }

  private constructor() {}

  public static getInstance(): MapService {
    if (!MapService.instance) {
      MapService.instance = new MapService();
    }
    return MapService.instance;
  }

  public setMapTiles(tiles: ITile[]): void {
    this.mapTiles = tiles;
    this.tileMap.clear();
    for (const t of tiles) {
      this.tileMap.set(`${t.x},${t.y}`, t);
    }
  }

  public getTileAt(x: number, y: number): ITile | undefined {
    return this.tileMap.get(`${x},${y}`);
  }

  public getMapTiles(): ITile[] {
    return this.mapTiles;
  }

  public reset(): void {
    this.mapTiles = [];
    this.tileMap.clear();
    this.sealedOriginals.clear();
    this.rooms = [];
    this.roomIndexMap.clear();
    this._generationDebug = null;
    this._discoveredTiles.clear();
  }

  public setGenerationDebug(debug: GenerationDebug | null): void {
    this._generationDebug = debug;
  }

  public getGenerationDebug(): GenerationDebug | null {
    return this._generationDebug;
  }

  // Seal bookkeeping is keyed by coordinate; clearing it here (the genuine
  // per-floor signal, unlike setMapTiles which the render bridge re-fires on
  // every seal/restore) prevents a stale original tile type from a prior floor
  // poisoning a same-coordinate seal on the new floor.
  public setRooms(rooms: IRoom[]): void {
    this.rooms = rooms;
    this.roomIndexMap.clear();
    rooms.forEach((room, index) => {
      for (const c of room.cells) {
        this.roomIndexMap.set(`${c.x},${c.y}`, index);
      }
    });
    this.sealedOriginals.clear();
  }

  public getRooms(): IRoom[] {
    return this.rooms;
  }

  public getRoomAt(x: number, y: number): IRoom | undefined {
    const index = this.roomIndexMap.get(`${x},${y}`);
    return index === undefined ? undefined : this.rooms[index];
  }

  public getRoomIndexAt(x: number, y: number): number {
    return this.roomIndexMap.get(`${x},${y}`) ?? -1;
  }

  public getRoomCells(room: IRoom): IPosition[] {
    return room.cells;
  }

  /**
   * `interact` Locations are treated as walls: they are part of the map and do
   * not depend on `ignoreEntities`. `walk` Locations (traps) stay traversable.
   *
   * Historical bug fixed: `getEntityAt(x,y)?.isWalkableOver ?? false` fell back
   * to `false` when no entity was present, which made every empty cell count as
   * blocked by an entity — A* pathfinding then failed and fell back to
   * `ignoreEntities=true`, so monsters planned through entities and got stuck.
   */
  public isWalkable(x: number, y: number, ignoreEntities = false): boolean {
    if (isTileBlocking(x, y, false)) {
      return false;
    }
    if (dungeonService.locationService.isLocationBlockingAt(x, y)) {
      return false;
    }
    if (!ignoreEntities) {
      const entity = dungeonService.getEntityAt(x, y);
      if (entity && !entity.isWalkableOver) {
        return false;
      }
    }
    return true;
  }

  public isDiagonalValid(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
  ): boolean {
    const dx = toX - fromX;
    const dy = toY - fromY;

    if (dx === 0 || dy === 0) {
      return true;
    }

    const tile1 = this.getTileAt(fromX, toY);
    const tile2 = this.getTileAt(toX, fromY);

    if (tile1?.type === 'wall' || tile2?.type === 'wall') {
      return false;
    }

    return true;
  }

  public getNeighbors(x: number, y: number): IPosition[] {
    return [
      { x: x + 1, y }, // RIGHT
      { x: x - 1, y }, // LEFT
      { x, y: y + 1 }, // TOP
      { x, y: y - 1 }, // BOTTOM
      { x: x + 1, y: y + 1 }, // TOP-RIGHT
      { x: x - 1, y: y + 1 }, // TOP-LEFT
      { x: x + 1, y: y - 1 }, // BOTTOM-RIGHT
      { x: x - 1, y: y - 1 }, // BOTTOM-LEFT
    ];
  }

  /**
   * Room cells that border ground outside the room's cell set — the corridor
   * mouths to seal when closing the room off.
   */
  public getRoomPerimeterOpenings(room: IRoom): IPosition[] {
    const inRoom = new Set(room.cells.map((c) => `${c.x},${c.y}`));
    const openings: IPosition[] = [];
    const cardinals = [
      { x: 1, y: 0 },
      { x: -1, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: -1 },
    ];
    for (const c of room.cells) {
      for (const d of cardinals) {
        const nx = c.x + d.x;
        const ny = c.y + d.y;
        if (inRoom.has(`${nx},${ny}`)) {
          continue;
        }
        if (this.getTileAt(nx, ny)?.type === 'ground') {
          openings.push({ x: c.x, y: c.y });
          break;
        }
      }
    }
    return openings;
  }

  public sealCells(cells: IPosition[]): void {
    const changed: TileDelta[] = [];
    for (const c of cells) {
      const key = `${c.x},${c.y}`;
      const tile = this.tileMap.get(key);
      if (!tile || tile.type === 'wall') {
        continue;
      }
      if (!this.sealedOriginals.has(key)) {
        this.sealedOriginals.set(key, tile.type);
      }
      tile.type = 'wall';
      changed.push({ x: c.x, y: c.y, type: 'wall' });
    }
    if (changed.length > 0) {
      this.onTilesChanged();
      this.onTilesMutated(changed);
    }
  }

  public restoreCells(cells: IPosition[]): void {
    const changed: TileDelta[] = [];
    for (const c of cells) {
      const key = `${c.x},${c.y}`;
      const original = this.sealedOriginals.get(key);
      const tile = this.tileMap.get(key);
      if (original && tile) {
        tile.type = original;
        this.sealedOriginals.delete(key);
        changed.push({ x: c.x, y: c.y, type: original });
      }
    }
    if (changed.length > 0) {
      this.onTilesChanged();
      this.onTilesMutated(changed);
    }
  }

  /** Client: apply host-replicated tile changes (no seal bookkeeping — the host
   *  is the authority; we just mirror the resulting tile types). */
  public applyTileDeltas(deltas: TileDelta[]): void {
    let changed = false;
    for (const d of deltas) {
      const tile = this.tileMap.get(`${d.x},${d.y}`);
      if (tile && tile.type !== d.type) {
        tile.type = d.type;
        changed = true;
      }
    }
    if (changed) {
      this.onTilesChanged();
    }
  }
}

export const mapService = MapService.getInstance();
